import { ChangeDetectionStrategy, Component, computed, inject, signal, viewChild } from "@angular/core";

import { merge, Observable, of, OperatorFunction, Subject } from "rxjs";
import { catchError, debounceTime, distinctUntilChanged, filter, switchMap } from "rxjs/operators";

import { OrganizationHttpService } from "@admin/components/organizations/services/organization-http.service";
import { UserHttpService } from "@admin/components/users/services/user-http.service";
import { AuthService } from "@core/services/auth.service";
import { NgbTypeahead, NgbTypeaheadSelectItemEvent } from "@ng-bootstrap/ng-bootstrap";
import { BaseModalComponent } from "@shared/components/modal/base-modal.component";
import { ModalService } from "@shared/components/modal/modal.service";
import { CommonConstants } from "@shared/constants/common.constants";
import { User } from "@shared/models/user.model";
import { ToastService } from "@shared/services/toast.service";

export interface BaseShareFormState {
  recipient: string;
  selectedUser: User | null;
}

@Component({ template: "", changeDetection: ChangeDetectionStrategy.OnPush })
export abstract class BaseShareModalComponent<T extends BaseShareFormState> extends BaseModalComponent {
  protected readonly instance = viewChild<NgbTypeahead>("instance");
  focus$ = new Subject<string>();
  click$ = new Subject<string>();

  protected readonly _userHttpService = inject(UserHttpService);
  protected readonly _organizationHttpService = inject(OrganizationHttpService);
  protected readonly _toast = inject(ToastService);
  protected readonly _auth = inject(AuthService);
  protected readonly _modalService = inject(ModalService);

  protected readonly loading = signal<boolean>(false);
  protected readonly linkCopied = signal<boolean>(false);

  protected readonly user = computed(() => this._auth.currentUser());
  protected _isSelecting = false;

  searchUsers: OperatorFunction<string, readonly User[]> = (text$: Observable<string>) => {
    const debouncedText$ = text$.pipe(debounceTime(CommonConstants.SEARCH_DEBOUNCE_TIME_MS), distinctUntilChanged());
    const clicksWithClosedPopup$ = this.click$.pipe(filter(() => !this.instance()?.isPopupOpen()));
    const inputFocus$ = this.focus$;

    return merge(debouncedText$, inputFocus$, clicksWithClosedPopup$).pipe(
      switchMap((term) => {
        return this._userHttpService.searchUsers(term).pipe(catchError(() => of([])));
      })
    );
  };

  formatter = (x: User): string => x.email || x.username || "";

  protected handleUserSelected(event: NgbTypeaheadSelectItemEvent<User>, updateFn: (u: User) => void): void {
    this._isSelecting = true;
    updateFn(event.item);
  }

  protected handleRecipientUpdate(value: string, currentState: T, updateFn: (s: Partial<T>) => void): void {
    if (this._isSelecting) {
      this._isSelecting = false;
      updateFn({ recipient: value } as any);
      return;
    }
    if (currentState.selectedUser) {
      const expected = this.formatter(currentState.selectedUser);
      if (value !== expected) {
        updateFn({ recipient: value, selectedUser: null } as any);
        return;
      }
    }
    updateFn({ recipient: value } as any);
  }

  protected copyLinkToClipboard(url: string): void {
    navigator.clipboard.writeText(url).then(() => {
      this.linkCopied.set(true);
      setTimeout(() => this.linkCopied.set(false), 3000);
    });
  }

  protected runOrgShareFlow(
    isTurningOn: boolean,
    messages: {
      title: string;
      message: string | ((count: number) => string);
      confirmText: string;
    },
    executeFn: () => void,
    cancelFn: () => void
  ): void {
    if (!isTurningOn) {
      const message = typeof messages.message === "function" ? messages.message(0) : messages.message;
      // Unshare
      const modalRef = this._modalService.confirm(messages.title, message, messages.confirmText, {
        closeOtherModals: false
      });

      modalRef.then((confirmed) => {
        if (confirmed) executeFn();
        else cancelFn();
      });
    } else {
      // Share
      this._organizationHttpService.getMembersCount().subscribe({
        next: ({ count }) => {
          const message = typeof messages.message === "function" ? messages.message(count) : messages.message;
          const modalRef = this._modalService.confirm(messages.title, message, messages.confirmText, {
            closeOtherModals: false
          });
          modalRef.then((confirmed) => {
            if (confirmed) executeFn();
            else cancelFn();
          });
        }
      });
    }
  }
}
